import type { TireResult } from '../../types';
import { Card } from '../shared/Card';
import { Badge } from '../shared/Badge';
import { getTirePositionLabel, getWearPatternLabel } from '../../lib/utils';

interface TireResultCardProps {
  tire: TireResult;
  className?: string;
  onRetake?: () => void;
}

const getConfidenceColor = (confidence: number) => {
  if (confidence >= 0.75) return 'text-green-600 bg-green-50';
  if (confidence >= 0.55) return 'text-amber-600 bg-amber-50';
  return 'text-red-600 bg-red-50';
};

const getConfidenceLabel = (confidence: number) => {
  if (confidence >= 0.75) return 'High';
  if (confidence >= 0.55) return 'Medium';
  return 'Low';
};

export const TireResultCard = ({ tire, className, onRetake }: TireResultCardProps) => {
  const confidence = tire.overall_confidence ?? 0.75;
  const needsRecheck = tire.needs_recheck ?? false;
  const isEstimated = tire.is_estimated ?? false;

  return (
    <Card className={`${className} ${needsRecheck ? 'ring-2 ring-amber-300' : ''}`}>
      <div className="flex items-start justify-between mb-3">
        <h4 className="text-lg font-semibold text-gray-900">
          {getTirePositionLabel(tire.position)}
        </h4>
        <div className="flex items-center gap-2">
          {/* Confidence indicator */}
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getConfidenceColor(confidence)}`}>
            {getConfidenceLabel(confidence)} conf.
          </span>
          <Badge status={tire.status} />
        </div>
      </div>

      {/* Low confidence warning */}
      {needsRecheck && tire.recheck_suggestion && (
        <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="flex-1">
              <p className="text-sm text-amber-800">{tire.recheck_suggestion}</p>
              {onRetake && (
                <button
                  onClick={onRetake}
                  className="mt-2 text-sm font-medium text-amber-700 hover:text-amber-800 underline"
                >
                  Retake photo
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        <div className="flex items-baseline gap-2">
          <div className="text-3xl font-bold text-gray-900">
            {tire.tread_mm.toFixed(1)}
          </div>
          <div className="text-sm text-gray-600">
            mm tread depth
            {isEstimated && (
              <span className="ml-1 text-amber-600">(estimated)</span>
            )}
          </div>
        </div>

        {/* Accuracy note */}
        {tire.accuracy_note && (
          <div className="text-xs text-gray-500 italic">
            {tire.accuracy_note}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100">
          <div>
            <div className="text-xs text-gray-500 uppercase font-medium">
              Wear Pattern
            </div>
            <div className="text-sm text-gray-900 mt-0.5">
              {getWearPatternLabel(tire.wear_pattern)}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase font-medium">
              Age
            </div>
            <div className="text-sm text-gray-900 mt-0.5">
              {tire.age_years.toFixed(1)} years
            </div>
          </div>
        </div>

        {/* Confidence breakdown (collapsible) */}
        {tire.confidence_factors && (
          <details className="pt-2 border-t border-gray-100">
            <summary className="text-xs text-gray-500 uppercase font-medium cursor-pointer hover:text-gray-700">
              Confidence Details
            </summary>
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-500">Detection:</span>
                <span className="font-medium">{Math.round(tire.confidence_factors.detection * 100)}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Image Quality:</span>
                <span className="font-medium">{Math.round(tire.confidence_factors.image_quality * 100)}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Segmentation:</span>
                <span className="font-medium">{Math.round(tire.confidence_factors.segmentation * 100)}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Sharpness:</span>
                <span className="font-medium">{Math.round(tire.confidence_factors.sharpness * 100)}%</span>
              </div>
            </div>
          </details>
        )}

        <div className="pt-2 border-t border-gray-100">
          <div className="text-xs text-gray-500 uppercase font-medium mb-1">
            DOT Code
          </div>
          <div className="text-xs font-mono text-gray-700 bg-gray-50 px-2 py-1 rounded">
            {tire.dot_code}
          </div>
        </div>
      </div>
    </Card>
  );
};
